import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { filter, firstValueFrom, take } from 'rxjs';
import { MapHelperService } from 'src/services/map-helper.service';
import { FeatureCollection, Polygon } from '@turf/helpers';
import { environment } from 'src/environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { LayerAPIService } from 'src/app/features/shared/services/API/LayersAPI.service';
import { HierarchyLevel } from '../../polygon-selector-with-levels/models/polygon-selector-with-levels.models';
import { EnumPolygonFillType } from '../../polygon-selector/helpers/enums/EnumPolygonFillType.enum';

@Injectable({
  providedIn: 'root'
})
export class TwoLevelsSelectorLayerControlService {
  hierarchyLevels: HierarchyLevel[] = [];

  private sourcePrefix = 'two-levels-selector-source-potential-zones-';
  private layerPrefix = 'layer-two-levels-selector-potential-zones-';
  private layerLinePrefix = 'layer-line-two-levels-selector-potential-zones-';
  private layerTextPrefix = 'layer-two-levels-selector-text-potential-zones-';

  private levelLineColors: string[] = ['rgb(140, 0, 255)', 'rgb(34, 59, 83)', '#e55e5e', '#1329f0', '#51bbd6',];
  private levelFillColors: string[] = ['rgb(160, 200, 240)', 'rgb(144, 224, 240)', '#f9f4a5', '#627e90', '#f28b82'];

  private isInitialized: boolean = false;

  popUp: any = null;
  hoveredMarker: any = null;
  hoveredMarkerFeature: any = null;

  constructor(
    private mapHelperService: MapHelperService,
    private http: HttpClient,
    private layerAPIService: LayerAPIService,
    private dialog: MatDialog,
    private toastrService: ToastrService
  ) {}

  async loadLayersFromAPI(idMap: number): Promise<HierarchyLevel[]> {
    const resp = await firstValueFrom( this.layerAPIService.GetLayersByIdMap(idMap.toString()).pipe(take(1)) );

    if (!resp.isSuccess) {
      console.error("selector, error cargando capas desde API", resp);
      return [];
    }

    const layers = resp.result as any[];
    this.hierarchyLevels = [];

    const sortedLayers = layers.sort((a, b) => (a.idOrder || 0) - (b.idOrder || 0));

    sortedLayers.forEach((layer, index) => {
      const hierarchyLevel: HierarchyLevel = { 
        key: layer.name || layer.id.toString(),
        path: layer.path || '',
        parentIdProperty: null,
        idProperty: layer.layerFilterControl_PropertyFilter || 'Id',
        sourceName: this.getSourceName(index),
        layerName: this.getLayerName(index),
        layerLineName: this.getLayerLineName(index),
        layerTextName: this.getLayerTextName(index)
      };
      this.hierarchyLevels.push(hierarchyLevel);
    });

    return this.hierarchyLevels;
  }

  async loadLayersFromAssetsDummy(): Promise<HierarchyLevel[]> {
    this.hierarchyLevels = [
      { 
        key: 'regions', 
        path: 'assets/regions_geoJson_dummy/regiones/regiones.geojson', 
        parentIdProperty: null, 
        idProperty: 'Id',
        sourceName: this.getSourceName(0),
        layerName: this.getLayerName(0),
        layerLineName: this.getLayerLineName(0),
        layerTextName: this.getLayerTextName(0),
      },
      { 
        key: 'agencies', 
        path: 'assets/regions_geoJson_dummy/zonas/zonas.geojson', 
        parentIdProperty: 'RegionId', 
        idProperty: 'Id',
        sourceName: this.getSourceName(1),
        layerName: this.getLayerName(1),
        layerLineName: this.getLayerLineName(1),
        layerTextName: this.getLayerTextName(1),
      }
    ];
    
    return this.hierarchyLevels;
  }

  getHierarchyLevels(): HierarchyLevel[] {
    return this.hierarchyLevels;
  }

  async createAllSourcesAndLayers(): Promise<void> {
    // if (!this.mapHelperService._map || !this.mapHelperService.isMainSourceReady) {
    //   console.error('LayerControl: Mapa no está listo');
    //   return;
    // }

    if (!this.mapHelperService._map || !this.mapHelperService.isMainSourceReady || !this.mapHelperService.isStyleReady) {
        await firstValueFrom(
          this.mapHelperService.mainSource$.pipe(
            filter(state => state != null && state.isMainSourceReady && state.isStyleReady),
            take(1)
          )
        );
      }

    if (this.isInitialized) {
      return;
    }

    // crea sources y layers para los niveles iniciales
    this.hierarchyLevels.forEach((level, index) => {
      if (index >= this.hierarchyLevels.length -1)
        return;
      this.createSourceAndLayersForLevel(index);
    });
    this.isInitialized = true;
  }

  private createSourceAndLayersForLevel(levelIndex: number): void {
    var sourceDisplayName = "";
    var sourceIcon = "";
    switch (levelIndex) {
      case 0: 
        sourceDisplayName = "Region seleccionada";
        sourceIcon = 'annotation-potential-zones-region.png';
        break;
      case 1:
        sourceDisplayName = "Zona seleccionada";
        sourceIcon = 'annotation-potential-zones-zone.png';
        break;
      default:
        sourceDisplayName = "Zonas nivel " + levelIndex;
        sourceIcon = 'annotation-polygon-selector.png';
        break;
    }
    const sourceName = this.getSourceName(levelIndex);
    const layerName = this.getLayerName(levelIndex);
    const layerLineName = this.getLayerLineName(levelIndex);
    const layerTextName = this.getLayerTextName(levelIndex);

    const existingSource = this.mapHelperService.GetSourceByName(sourceName);
    
    if (!existingSource) {
      const color = this.getColorForLevel(levelIndex, this.levelFillColors);
      const lineColor = this.getColorForLevel(levelIndex, this.levelLineColors);

      this.mapHelperService.AddSourceEmptyGeoJson(sourceName, sourceDisplayName, sourceIcon);
      this.mapHelperService.AddLayerPolygonsWithFeatureStatus(layerName, sourceName, "Polígonos de zonas, nivel " + levelIndex, color);
      this.mapHelperService.AddLayerLine(layerLineName, sourceName, "Líneas de zonas, nivel " + levelIndex, lineColor, 3);
      this.mapHelperService.AddLayerText(layerTextName, sourceName, 'Description', "Etiqueta de zonas, nivel " + levelIndex, -1);
      
    } else {
      console.log("ya existe el source para nivel " + levelIndex);
    }
  }

  // métodos para obtener nombres
  getSourceName(levelIndex: number): string {
    return this.sourcePrefix + levelIndex;
  }

  getLayerName(levelIndex: number): string {
    return this.layerPrefix + levelIndex;
  }

  getLayerLineName(levelIndex: number): string {
    return this.layerLinePrefix + levelIndex;
  }

  getLayerTextName(levelIndex: number): string {
    return this.layerTextPrefix + levelIndex;
  }

  private getColorForLevel(levelIndex: number, colors: string[]): string {
    return colors[levelIndex % colors.length];
  }

  destroy(): void {
    this.isInitialized = false;
    this.hierarchyLevels.forEach((level, index) => {
      const sourceName = this.getSourceName(index);
      this.mapHelperService.UpdateLayerGeoJson(sourceName, this.mapHelperService.Empty_GeoJson);
    });
  }
}
